import { FILTER_MIXPANEL_TRACKING_DEBOUNCE } from "@jidlomat/terminal-core/config/constants";
import { useMixpanelTrackers } from "@jidlomat/terminal-core/hooks/useMixpanelTrackers";
import { useAppDispatch, useAppSelector } from "@jidlomat/terminal-core/hooks/utilsHooks/storeHooks";
import {
	selectFilterDebounceTimeoutId,
	selectFilters,
	setFilterDebounceTimeoutId,
	setFilters,
} from "@jidlomat/terminal-core/slices/appStateSlice";
import type { Filters } from "@jidlomat/terminal-core/types/mainTypes";
import { useCallback } from "react";

export const useFilters = () => {
	const filters = useAppSelector(selectFilters);
	const filterDebounceTimeoutId = useAppSelector(selectFilterDebounceTimeoutId);
	const dispatch = useAppDispatch();
	const { trackManualFilterApplied } = useMixpanelTrackers();

	const clearTrackingTimeout = () => {
		if (filterDebounceTimeoutId !== null) {
			window.clearTimeout(filterDebounceTimeoutId);
			dispatch(setFilterDebounceTimeoutId(null));
		}
	};

	const handleSetFilters = useCallback(
		(payload: Filters, immediate = false) => {
			if (!payload.allergens) {
				return;
			}

			// Clear any pending timeout
			clearTrackingTimeout();

			dispatch(setFilters(payload));

			if (immediate) {
				trackManualFilterApplied(payload.allergens);
			} else {
				const allergensSnapshot = payload.allergens;
				const timeoutId = window.setTimeout(() => {
					trackManualFilterApplied(allergensSnapshot);
				}, FILTER_MIXPANEL_TRACKING_DEBOUNCE);
				dispatch(setFilterDebounceTimeoutId(timeoutId));
			}
		},
		// biome-ignore lint/correctness/useExhaustiveDependencies: clearTrackingTimeout is stable enough for this callback
		[trackManualFilterApplied, clearTrackingTimeout, dispatch],
	);

	const resetAllergens = useCallback(() => {
		if (filters.allergens.length > 0) {
			handleSetFilters({ allergens: [] }, true);
		}
	}, [filters.allergens.length, handleSetFilters]);

	return {
		allergens: filters.allergens,
		setFilters: handleSetFilters,
		resetAllergens: resetAllergens,
	};
};
